import { createDraftSafeSelector } from '@reduxjs/toolkit'
import { shallowEqual } from 'react-redux'
import { OptionChainViewKey, OptionChainViewType } from '../assets/constants'
import { Security } from '../proto/generated/security'
import { RootState } from '../store'
import { WidgetDashboardConfig } from '../components/widgetDashboard/widgetDashboard.interface'
import { TimeBucket } from '../proto/generated/common'
import { PrimarySecurity } from '../util/security'
import { timeIntervalValues } from '../util/tradePage'
import {
    defaultExtendedOptionChainColumns,
    defaultOptionChainColumns,
    getAllOptionChainColumnIds,
    getCanonicalOptionChainColumnOrder,
    getVisibleOptionChainColumns,
    mergeIntoColumnOrder,
    normalizeOptionChainColumnOrder,
    normalizeOptionChainHiddenColumns,
} from '../util/optionChainColumns'

export { defaultExtendedOptionChainColumns, defaultOptionChainColumns }

export type { PrimarySecurity } from '../util/security'

const state = (state: RootState) => state
const selectHomeStore = (s: RootState) => s.homeStore

const EMPTY_EXPRESSION_SECURITIES: Security[] = []

const buildSkeletonPrimarySecurity = (primarySecurity: RootState['homeStore']['primarySecurity']): PrimarySecurity => ({
    key: primarySecurity.key,
    displayName: primarySecurity.displayName,
    expressionMode: primarySecurity.expressionMode,
    expressionSecurities: EMPTY_EXPRESSION_SECURITIES,
    underlyingSecurityCode: primarySecurity.underlyingSecurityCode
})

/** Input is `homeStore` only — snapshot ticks do not invalidate this selector. */
export const selectPrimarySecurity = createDraftSafeSelector(
    selectHomeStore,
    (homeStore): PrimarySecurity =>
        homeStore.resolvedPrimarySecurity ?? buildSkeletonPrimarySecurity(homeStore.primarySecurity)
)

export const selectUnderlyingSecurity = createDraftSafeSelector(state, (state: RootState): any => {
    const { key, expressionMode, underlyingSecurityCode, securities: stateSecurities } = state.homeStore.primarySecurity
    const resolved = state.homeStore.resolvedPrimarySecurity
    const securities: Security[] = underlyingSecurityCode
        ? (resolved?.underlying ? [resolved.underlying] : [])
        : (resolved?.expressionSecurities ?? [])
    return {
        key,
        expressionMode,
        tokens: underlyingSecurityCode ? [underlyingSecurityCode] : stateSecurities,
        securities
    }
})

export const selectOptionChainView = (viewKey: OptionChainViewKey) => createDraftSafeSelector(state, (state: RootState): OptionChainViewType => {
    return state.homeStore.optionChain[viewKey].view
})

export const selectOptionChainMirrorScroll = (viewKey: OptionChainViewKey, type: 'extended' | 'normal') => createDraftSafeSelector(state, (state: RootState) => {
    return state.commonStore.optionChain?.[viewKey]?.[type]?.isMirrorScroll as boolean
})

export const selectExpandedRows = (viewKey: OptionChainViewKey) => createDraftSafeSelector(state, (state: RootState): string[] => {
    return state.homeStore.optionChain[viewKey]?.expandedRows ?? ['0']
})

export const checkExpandedRowsContainRow = (key: string, viewKey: OptionChainViewKey) => createDraftSafeSelector(state, (state: RootState): boolean => {
    return state.homeStore.optionChain[viewKey].expandedRows.includes(key)
})

export const selectOptionChainTimeInterval = (viewKey: OptionChainViewKey, type: 'extended' | 'normal') => createDraftSafeSelector(state, (state: RootState): TimeBucket => {
    return (state.commonStore.optionChain?.[viewKey]?.[type]?.timeInterval) ?? (timeIntervalValues[timeIntervalValues.length - 1].value)
})

const EMPTY_HIDDEN_COLUMNS: string[] = []

export type OptionChainColumnState = {
    order: string[]
    hidden: string[]
    visible: string[]
}

const computeOptionChainColumnState = (
    slice: RootState['commonStore']['optionChain'][OptionChainViewKey]['normal'] | undefined,
    type: 'extended' | 'normal'
): OptionChainColumnState => {
    const defaults = type === 'extended' ? defaultExtendedOptionChainColumns : defaultOptionChainColumns
    const savedOrder = normalizeOptionChainColumnOrder(slice?.optionSelectedColumns ?? defaults)
    const hidden = normalizeOptionChainHiddenColumns(slice?.optionHiddenColumns ?? EMPTY_HIDDEN_COLUMNS)
    const order = mergeIntoColumnOrder(savedOrder, getAllOptionChainColumnIds(type), getCanonicalOptionChainColumnOrder(type))
    const visible = getVisibleOptionChainColumns(order, hidden)
    return { order, hidden, visible }
}

const optionChainColumnStateSelectorCache = new Map<string, ReturnType<typeof createDraftSafeSelector>>()

const makeSelectOptionChainColumnState = (viewKey: OptionChainViewKey, type: 'extended' | 'normal') => {
    const cacheKey = `${viewKey}:${type}`
    if (!optionChainColumnStateSelectorCache.has(cacheKey)) {
        optionChainColumnStateSelectorCache.set(
            cacheKey,
            createDraftSafeSelector(
                [(root: RootState) => root.commonStore.optionChain?.[viewKey]?.[type]],
                (slice) => computeOptionChainColumnState(slice, type),
                {
                    memoizeOptions: {
                        resultEqualityCheck: (prev, next) =>
                            prev.order === next.order &&
                            prev.hidden === next.hidden &&
                            prev.visible === next.visible,
                    },
                }
            )
        )
    }
    return optionChainColumnStateSelectorCache.get(cacheKey)! as (state: RootState) => OptionChainColumnState
}

const optionChainColumnFieldSelectorCache = new Map<string, ReturnType<typeof createDraftSafeSelector>>()

const makeSelectOptionChainColumnField = <K extends keyof OptionChainColumnState>(
    viewKey: OptionChainViewKey,
    type: 'extended' | 'normal',
    field: K
) => {
    const cacheKey = `${viewKey}:${type}:${field}`
    if (!optionChainColumnFieldSelectorCache.has(cacheKey)) {
        const baseSelector = makeSelectOptionChainColumnState(viewKey, type)
        optionChainColumnFieldSelectorCache.set(
            cacheKey,
            createDraftSafeSelector(
                [baseSelector],
                (columnState) => columnState[field],
                field === 'visible'
                    ? { memoizeOptions: { resultEqualityCheck: shallowEqual } }
                    : undefined
            )
        )
    }
    return optionChainColumnFieldSelectorCache.get(cacheKey)! as (state: RootState) => OptionChainColumnState[K]
}

export const selectOptionChainColumnState = (viewKey: OptionChainViewKey, type: 'extended' | 'normal') =>
    makeSelectOptionChainColumnState(viewKey, type)

export const selectOptionChainColumnOrder = (viewKey: OptionChainViewKey, type: 'extended' | 'normal') =>
    makeSelectOptionChainColumnField(viewKey, type, 'order')

export const selectOptionChainHiddenColumns = (viewKey: OptionChainViewKey, type: 'extended' | 'normal') =>
    makeSelectOptionChainColumnField(viewKey, type, 'hidden')

export const selectOptionChainColumns = (viewKey: OptionChainViewKey, type: 'extended' | 'normal') =>
    makeSelectOptionChainColumnField(viewKey, type, 'visible')

export const selectOptionsExpiry = createDraftSafeSelector(state, (state: RootState): string => {
    return state.homeStore.fnoView.selectedOptionsExpiry
})

export const selectActiveLayout = (store: RootState) => store.widgetDashboardStore.layouts[store.homeStore.activeLayout] as WidgetDashboardConfig

export const selectActiveLayoutId = (store: RootState) => store.homeStore.activeLayout as string

export const selectLastActiveLayoutId = (store: RootState) => store.homeStore.lastActiveLayout as string

export const selectSecuritySearchOpen = (store: RootState) => store.homeStore.securitySearchOpen

export const selectFinancialPanelOpen = (store: RootState) => store.homeStore.financialPanelOpen

export const selectFinancialPanelSection = (store: RootState) => store.homeStore.financialPanelSection

export const selectIsPrimarySecurityLoading = (store: RootState) =>
    store.homeStore.resolvedPrimarySecurity === null && !!store.homeStore.primarySecurity?.key